import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import LanguageSelector, { useLanguage } from '../../components/feature/LanguageSelector';
import { translations } from '../../mocks/translations';
import GallerySlideshow from './components/GallerySlideshow';
import { supabase } from '../../utils/supabaseClient';
import { useSEO, generateOrganizationSchema } from '../../utils/seo';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const language = useLanguage();
  const t = translations[language];

  // SEO Configuration
  useSEO({
    title: 'Leury Tours Puerto Plata - Traslados Aeropuerto y Tours | República Dominicana',
    description: 'Traslados profesionales al aeropuerto y tours en Puerto Plata, República Dominicana. Reserve transporte confiable desde aeropuertos PUJ, SDQ, STI, POP. Excursiones, tours de ciudad y servicios personalizados.',
    keywords: 'tours Puerto Plata, traslados aeropuerto República Dominicana, traslado aeropuerto PUJ, transporte Punta Cana, tours Santo Domingo, Leury Tours, excursiones República Dominicana',
    ogImage: 'https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8ae7b2cd5c5fe056440d4fe4564ccd66.jpeg',
    schema: generateOrganizationSchema()
  });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    
    setNewsletterStatus('submitting');
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d67578b8scpqk7lbjn80', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          email: newsletterEmail,
          message: language === 'es' 
            ? 'Este cliente quiere recibir información sobre tours y transportes de Leury Tours'
            : 'This customer wants to receive information about Leury Tours tours and transportation'
        }).toString()
      });

      if (response.ok) {
        setNewsletterStatus('success');
        setNewsletterEmail('');
        setTimeout(() => setNewsletterStatus('idle'), 3000);
      } else {
        setNewsletterStatus('error');
        setTimeout(() => setNewsletterStatus('idle'), 3000);
      }
    } catch (error) {
      setNewsletterStatus('error');
      setTimeout(() => setNewsletterStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8ae7b2cd5c5fe056440d4fe4564ccd66.jpeg" alt="Leury Tours" className="h-10 sm:h-12 w-auto" />
              <span className={`text-lg sm:text-xl font-bold ${scrolled ? 'text-[#0A2463]' : 'text-white'}`}>Leury Tours</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              <Link to="/" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#00B4D8]' : 'text-[#48CAE4]'}`}>{t.nav.home}</Link>
              <Link to="/about" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>{t.nav.about}</Link>
              <Link to="/tours" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>{t.nav.tours}</Link>
              <Link to="/transfers" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>{t.nav.transfers}</Link>
              <a href="#reviews" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>{t.nav.reviews}</a>
              <Link to="/booking" className={`text-sm font-medium transition-colors ${scrolled ? 'text-[#0A2463] hover:text-[#00B4D8]' : 'text-white hover:text-[#48CAE4]'}`}>{t.nav.contact}</Link>
              <LanguageSelector scrolled={scrolled} />
              <Link to="/booking" className="bg-[#FF6B6B] text-white px-5 xl:px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#ff5252] transition-all whitespace-nowrap">{t.nav.bookNow}</Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center gap-3 lg:hidden">
              <LanguageSelector scrolled={scrolled} />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`p-2 rounded-lg ${scrolled ? 'text-[#0A2463]' : 'text-white'}`}
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-[60px] bg-white z-40 overflow-y-auto">
            <div className="px-4 py-6 space-y-4">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-base font-medium text-[#00B4D8] py-3 border-b border-gray-100">{t.nav.home}</Link>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100">{t.nav.about}</Link>
              <Link to="/tours" onClick={() => setMobileMenuOpen(false)} className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100">{t.nav.tours}</Link>
              <Link to="/transfers" onClick={() => setMobileMenuOpen(false)} className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100">{t.nav.transfers}</Link>
              <a href="#reviews" onClick={() => setMobileMenuOpen(false)} className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100">{t.nav.reviews}</a>
              <a href="#contact" onClick={() => setMobileMenuOpen(false)} className="block text-base font-medium text-[#0A2463] py-3 border-b border-gray-100">{t.nav.contact}</a>
              <Link to="/booking" onClick={() => setMobileMenuOpen(false)} className="block w-full bg-[#FF6B6B] text-white px-6 py-3 rounded-full text-base font-semibold text-center mt-6">{t.nav.bookNow}</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/443dc03b1345d2439df86607c25a1712.jpg" alt="Puerto Plata" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A2463]/70 via-[#0A2463]/40 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full pt-20 pb-32 sm:pt-0 sm:pb-0">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-4 sm:mb-6">
              <i className="ri-palm-tree-line text-[#48CAE4] text-lg sm:text-xl"></i>
              <span className="text-white/90 text-xs sm:text-sm font-medium tracking-wide">{t.home.heroSubtitle}</span>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-4 sm:mb-6">
              {t.home.heroTitle1}<br />{t.home.heroTitle2}<br />{t.home.heroTitle3}
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/90 mb-6 sm:mb-8 font-light">{t.home.heroDescription}</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/booking" className="bg-[#00B4D8] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#0096b8] transition-all flex items-center justify-center gap-2 whitespace-nowrap">
                {t.home.planTrip} <i className="ri-arrow-right-line"></i>
              </Link>
              <Link to="/tours" className="bg-transparent border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-white hover:text-[#0A2463] transition-all whitespace-nowrap text-center">
                {t.home.viewTours}
              </Link>
            </div>
          </div>
        </div>

        {/* Floating Cards - Hidden on mobile */}
        <div className="absolute bottom-12 left-0 right-0 z-20 hidden lg:block">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex gap-6 justify-center">
              <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-6 w-72 transform hover:scale-105 transition-all shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#00B4D8] rounded-full flex items-center justify-center">
                    <i className="ri-car-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base">{t.home.airportTransfers}</h3>
                    <p className="text-white/80 text-sm">{t.home.fromPrice}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-6 w-72 transform hover:scale-105 transition-all shadow-xl -mt-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#FF6B6B] rounded-full flex items-center justify-center">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base">{t.home.featuredTours}</h3>
                    <p className="text-white/80 text-sm">{t.home.exploreParadise}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/20 backdrop-blur-lg rounded-2xl p-6 w-72 transform hover:scale-105 transition-all shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#FFD700] rounded-full flex items-center justify-center">
                    <i className="ri-star-fill text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base">{t.home.fiveStarRated}</h3>
                    <p className="text-white/80 text-sm">{t.home.reviewsCount}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-white to-[#FFF9F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
            <div className="lg:col-span-2 lg:sticky lg:top-32 h-fit text-center lg:text-left">
              <span className="text-[#FF6B6B] text-xs sm:text-sm font-bold tracking-wider uppercase">{t.home.ourServices}</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2463] mt-3 sm:mt-4 mb-4 sm:mb-6 leading-tight">
                {t.home.servicesTitle1}<br className="hidden sm:block" />{t.home.servicesTitle2}<br className="hidden sm:block" />{t.home.servicesTitle3}
              </h2>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">{t.home.servicesDescription}</p>
              <div className="mt-4 sm:mt-6 hidden lg:block">
                <svg width="120" height="4" viewBox="0 0 120 4" fill="none">
                  <path d="M0 2C30 2 30 2 60 2C90 2 90 2 120 2" stroke="#00B4D8" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              </div>
            </div>

            <div className="lg:col-span-3 space-y-6 sm:space-y-8">
              {/* Airport Transfers Card */}
              <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group">
                <div className="grid md:grid-cols-2">
                  <div className="relative h-48 sm:h-64 md:h-auto">
                    <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/adacd0c79cdca09a503af18efa205f12.jpeg" alt="Airport Transfer" className="w-full h-full object-cover object-center" />
                  </div>
                  <div className="p-5 sm:p-6 lg:p-8 flex flex-col justify-center">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#00B4D8]/10 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <i className="ri-plane-line text-[#00B4D8] text-xl sm:text-2xl"></i>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0A2463] mb-2 sm:mb-3">{t.home.airportTransfersTitle}</h3>
                    <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed">{t.home.airportTransfersDesc}</p>
                    <p className="text-[#00B4D8] font-bold text-base sm:text-lg mb-4 sm:mb-6">{t.home.fromPrice}</p>
                    <Link to="/transfers" className="inline-flex items-center gap-2 border-2 border-[#00B4D8] text-[#00B4D8] px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold hover:bg-[#00B4D8] hover:text-white transition-all w-fit text-xs sm:text-sm whitespace-nowrap">
                      {t.home.viewOptions} <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Excursions Card */}
              <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group">
                <div className="grid md:grid-cols-2">
                  <div className="relative h-48 sm:h-64 md:h-auto">
                    <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/93295514ce5b61fdd36eaa85813a70ed.jpeg" alt="Excursions" className="w-full h-full object-cover object-center" />
                    <div className="absolute inset-0 bg-[#FF6B6B]/20"></div>
                  </div>
                  <div className="p-5 sm:p-6 lg:p-8 flex flex-col justify-center">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#FF6B6B]/10 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <i className="ri-compass-3-line text-[#FF6B6B] text-xl sm:text-2xl"></i>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0A2463] mb-2 sm:mb-3">{t.home.excursionsTitle}</h3>
                    <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed">{t.home.excursionsDesc}</p>
                    <p className="text-[#FF6B6B] font-bold text-base sm:text-lg mb-1 sm:mb-2">{t.home.privateGroupAvailable}</p>
                    <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">{t.home.fromPerson}</p>
                    <Link to="/tours" className="inline-flex items-center gap-2 border-2 border-[#FF6B6B] text-[#FF6B6B] px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold hover:bg-[#FF6B6B] hover:text-white transition-all w-fit text-xs sm:text-sm whitespace-nowrap">
                      {t.home.exploreTours} <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>

              {/* City Tours Card */}
              <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group">
                <div className="grid md:grid-cols-2">
                  <div className="relative h-48 sm:h-64 md:h-auto">
                    <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/c4a53c3f95678eead380d1fae0d81e0a.jpeg" alt="City Tours" className="w-full h-full object-cover object-center" />
                  </div>
                  <div className="p-5 sm:p-6 lg:p-8 flex flex-col justify-center">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#48CAE4]/10 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <i className="ri-building-line text-[#48CAE4] text-xl sm:text-2xl"></i>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0A2463] mb-2 sm:mb-3">{t.home.cityToursTitle}</h3>
                    <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed">{t.home.cityToursDesc}</p>
                    <p className="text-[#48CAE4] font-bold text-base sm:text-lg mb-1 sm:mb-2">{t.home.duration}</p>
                    <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">{t.home.includes}</p>
                    <Link to="/tours#city-tour" className="inline-flex items-center gap-2 border-2 border-[#48CAE4] text-[#48CAE4] px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold hover:bg-[#48CAE4] hover:text-white transition-all w-fit text-xs sm:text-sm whitespace-nowrap">
                      {t.home.viewDetails} <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>

              {/* Custom Services Card */}
              <div className="bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group">
                <div className="grid md:grid-cols-2">
                  <div className="relative h-48 sm:h-64 md:h-auto">
                    <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/7da2ac34d707b8756ed44f663f8c145b.png" alt="Custom Services" className="w-full h-full object-cover object-center" />
                  </div>
                  <div className="p-5 sm:p-6 lg:p-8 flex flex-col justify-center">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#FFD700]/10 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <i className="ri-star-line text-[#FFD700] text-xl sm:text-2xl"></i>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0A2463] mb-2 sm:mb-3">{t.home.customServicesTitle}</h3>
                    <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed">{t.home.customServicesDesc}</p>
                    <p className="text-[#FFD700] font-bold text-base sm:text-lg mb-4 sm:mb-6">{t.home.tailoredToYou}</p>
                    <Link to="/booking" className="inline-flex items-center gap-2 border-2 border-[#FFD700] text-[#FFD700] px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold hover:bg-[#FFD700] hover:text-white transition-all w-fit text-xs sm:text-sm whitespace-nowrap">
                      {t.home.contactUs} <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Puerto Plata Attractions Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-[#0A2463] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0c16.569 0 30 13.431 30 30 0 16.569-13.431 30-30 30C13.431 60 0 46.569 0 30 0 13.431 13.431 0 30 0z\' fill=\'%23ffffff\' fill-opacity=\'1\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-4">{t.home.discoverPuertoPlata}</h2>
            <p className="text-[#48CAE4] text-sm sm:text-base lg:text-lg mb-4 sm:mb-6">{t.home.puertoPlataSubtitle}</p>
            <p className="text-white/80 max-w-3xl mx-auto text-xs sm:text-sm lg:text-base leading-relaxed mb-6 sm:mb-8">{t.home.puertoPlataDescription}</p>
            
            {/* New Attractions Description */}
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border border-white/20">
              <p className="text-white/90 text-xs sm:text-sm lg:text-base leading-relaxed">
                {language === 'es' ? (
                  <>
                    Puerto Plata ofrece una increíble variedad de experiencias para todos los gustos. Desde la emocionante aventura de saltar por las <strong className="text-[#48CAE4]">27 Cascadas de Damajagua</strong>, hasta las vistas panorámicas desde el <strong className="text-[#48CAE4]">Teleférico</strong> que sube al Monte Isabel de Torres. Explora la rica historia colonial en la <strong className="text-[#48CAE4]">Fortaleza San Felipe</strong>, descubre tesoros prehistóricos en el <strong className="text-[#48CAE4]">Museo del Ámbar</strong>, o relájate en las hermosas playas de <strong className="text-[#48CAE4]">Playa Dorada</strong> y <strong className="text-[#48CAE4]">Sosúa</strong>. Para los amantes de los deportes acuáticos, <strong className="text-[#48CAE4]">Cabarete</strong> es el paraíso del kitesurf y windsurf. Las familias disfrutarán de encuentros únicos con delfines en <strong className="text-[#48CAE4]">Ocean World</strong>. Cada atracción cuenta una historia diferente de esta fascinante región del Caribe.
                  </>
                ) : (
                  <>
                    Puerto Plata offers an incredible variety of experiences for all tastes. From the thrilling adventure of jumping through the <strong className="text-[#48CAE4]">&nbsp;Waterfalls of Damajagua</strong>, to the panoramic views from the <strong className="text-[#48CAE4]">Cable Car</strong> ascending Mount Isabel de Torres. Explore the rich colonial history at <strong className="text-[#48CAE4]">Fort San Felipe</strong>, discover prehistoric treasures at the <strong className="text-[#48CAE4]">Amber Museum</strong>, or relax on the beautiful beaches of <strong className="text-[#48CAE4]">Playa Dorada</strong> and <strong className="text-[#48CAE4]">Sosúa</strong>. For water sports enthusiasts, <strong className="text-[#48CAE4]">Cabarete</strong> is the kitesurfing and windsurfing paradise. Families will enjoy unique dolphin encounters at <strong className="text-[#48CAE4]">Ocean World</strong>. Each attraction tells a different story of this fascinating Caribbean region.
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Attractions Grid - Mobile optimized */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-4 sm:gap-6 mt-8 sm:mt-12">
            {/* Teleférico - Large Card */}
            <div className="sm:col-span-2 lg:col-span-7 group cursor-pointer">
              <div className="relative h-64 sm:h-80 lg:h-[420px] rounded-2xl sm:rounded-3xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Puerto%20Plata%20cable%20car%20teleferico%20ascending%20Mount%20Isabel%20de%20Torres%20with%20stunning%20panoramic%20view%20of%20city%20and%20turquoise%20Caribbean%20coastline%20below%2C%20tropical%20green%20mountains%2C%20Christ%20Redeemer%20statue%20visible%20at%20top%2C%20iconic%20Dominican%20Republic%20landmark%2C%20professional%20tourism%20photography%2C%20bright%20sunny%20day%2C%20lush%20vegetation&width=900&height=600&seq=teleferico1&orientation=landscape" 
                  alt="Teleférico Puerto Plata" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <span className="bg-[#48CAE4] text-[#0A2463] text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full">TOP ATTRACTION</span>
                  </div>
                  <h3 className="text-white text-xl sm:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3">{t.home.telefericoTitle}</h3>
                  <p className="text-white/90 text-xs sm:text-sm leading-relaxed line-clamp-2 sm:line-clamp-none">{t.home.telefericoDesc}</p>
                </div>
              </div>
            </div>

            {/* 27 Waterfalls */}
            <div className="lg:col-span-5 group cursor-pointer">
              <div className="relative h-64 sm:h-80 lg:h-[420px] rounded-2xl sm:rounded-3xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=27%20Waterfalls%20of%20Damajagua%20Dominican%20Republic%20with%20people%20jumping%20into%20crystal%20clear%20turquoise%20natural%20pool%2C%20cascading%20waterfall%20surrounded%20by%20lush%20tropical%20jungle%20vegetation%2C%20adventure%20tourism%2C%20professional%20nature%20photography%2C%20bright%20daylight%2C%20exciting%20water%20adventure&width=700&height=600&seq=damajagua1&orientation=portrait" 
                  alt="27 Charcos de Damajagua" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 lg:p-8">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <span className="bg-emerald-400 text-emerald-900 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full">ADVENTURE</span>
                  </div>
                  <h3 className="text-white text-lg sm:text-xl lg:text-2xl font-bold mb-2 sm:mb-3">{t.home.damajaguaTitle}</h3>
                  <p className="text-white/90 text-xs sm:text-sm leading-relaxed line-clamp-2 sm:line-clamp-none">{t.home.damajaguaDesc}</p>
                </div>
              </div>
            </div>

            {/* Row 2 - Three Cards */}
            {/* Amber Museum */}
            <div className="lg:col-span-4 group cursor-pointer">
              <div className="relative h-56 sm:h-72 lg:h-[380px] rounded-2xl sm:rounded-3xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Dominican%20amber%20museum%20interior%20with%20beautiful%20Victorian%20mansion%20architecture%2C%20display%20cases%20showing%20golden%20amber%20specimens%20with%20prehistoric%20insects%2C%20elegant%20wooden%20staircase%2C%20Puerto%20Plata%20cultural%20heritage%2C%20professional%20museum%20photography%2C%20warm%20lighting&width=600&height=500&seq=amber1&orientation=portrait" 
                  alt="Museo del Ámbar" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <span className="bg-amber-400 text-amber-900 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full">CULTURE</span>
                  </div>
                  <h3 className="text-white text-base sm:text-lg lg:text-xl font-bold mb-1 sm:mb-2">{t.home.amberMuseumTitle}</h3>
                  <p className="text-white/90 text-[10px] sm:text-xs leading-relaxed line-clamp-2">{t.home.amberMuseumDesc}</p>
                </div>
              </div>
            </div>

            {/* Fort San Felipe */}
            <div className="lg:col-span-4 group cursor-pointer">
              <div className="relative h-56 sm:h-72 lg:h-[380px] rounded-2xl sm:rounded-3xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Fort%20San%20Felipe%20Puerto%20Plata%20historic%20colonial%20fortress%20with%20stone%20walls%20and%20cannons%2C%20overlooking%20Atlantic%20Ocean%2C%20blue%20sky%2C%20oldest%20fortress%20in%20Americas%2C%20Dominican%20Republic%20heritage%20site%2C%20professional%20architectural%20photography%2C%20dramatic%20lighting&width=600&height=500&seq=fort1&orientation=portrait" 
                  alt="Fortaleza San Felipe" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <span className="bg-orange-400 text-orange-900 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full">HISTORY</span>
                  </div>
                  <h3 className="text-white text-base sm:text-lg lg:text-xl font-bold mb-1 sm:mb-2">{t.home.fortSanFelipeTitle}</h3>
                  <p className="text-white/90 text-[10px] sm:text-xs leading-relaxed line-clamp-2">{t.home.fortSanFelipeDesc}</p>
                </div>
              </div>
            </div>

            {/* Playa Dorada */}
            <div className="lg:col-span-4 group cursor-pointer">
              <div className="relative h-56 sm:h-72 lg:h-[380px] rounded-2xl sm:rounded-3xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Playa%20Dorada%20Puerto%20Plata%20pristine%20golden%20sand%20beach%20with%20calm%20turquoise%20Caribbean%20waters%2C%20palm%20trees%2C%20luxury%20resort%20in%20background%2C%20beach%20umbrellas%2C%20perfect%20tropical%20paradise%2C%20professional%20travel%20photography%2C%20bright%20sunny%20day&width=600&height=500&seq=playa1&orientation=portrait" 
                  alt="Playa Dorada" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <span className="bg-cyan-400 text-cyan-900 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full">BEACH</span>
                  </div>
                  <h3 className="text-white text-base sm:text-lg lg:text-xl font-bold mb-1 sm:mb-2">{t.home.playaDoradaTitle}</h3>
                  <p className="text-white/90 text-[10px] sm:text-xs leading-relaxed line-clamp-2">{t.home.playaDoradaDesc}</p>
                </div>
              </div>
            </div>

            {/* Row 3 - Three Cards */}
            {/* Ocean World */}
            <div className="sm:col-span-2 lg:col-span-5 group cursor-pointer">
              <div className="relative h-56 sm:h-72 lg:h-[350px] rounded-2xl sm:rounded-3xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Ocean%20World%20Adventure%20Park%20Puerto%20Plata%20with%20dolphins%20jumping%20in%20crystal%20clear%20pool%2C%20tropical%20marine%20park%2C%20visitors%20watching%20dolphin%20show%2C%20palm%20trees%2C%20Caribbean%20setting%2C%20professional%20tourism%20photography%2C%20bright%20sunny%20day%2C%20family%20fun&width=700&height=500&seq=ocean1&orientation=landscape" 
                  alt="Ocean World" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <span className="bg-sky-400 text-sky-900 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full">FAMILY FUN</span>
                  </div>
                  <h3 className="text-white text-lg sm:text-xl lg:text-2xl font-bold mb-1 sm:mb-2">{t.home.oceanWorldTitle}</h3>
                  <p className="text-white/90 text-xs sm:text-sm leading-relaxed line-clamp-2">{t.home.oceanWorldDesc}</p>
                </div>
              </div>
            </div>

            {/* Sosúa Beach */}
            <div className="lg:col-span-3 group cursor-pointer">
              <div className="relative h-56 sm:h-72 lg:h-[350px] rounded-2xl sm:rounded-3xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Sosua%20Beach%20Dominican%20Republic%20crescent%20shaped%20bay%20with%20turquoise%20waters%2C%20snorkeling%20area%2C%20colorful%20coral%20reefs%20visible%2C%20beachfront%20restaurants%2C%20palm%20trees%2C%20professional%20aerial%20beach%20photography%2C%20tropical%20paradise&width=500&height=500&seq=sosua1&orientation=squarish" 
                  alt="Playa Sosúa" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <span className="bg-teal-400 text-teal-900 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full">SNORKELING</span>
                  </div>
                  <h3 className="text-white text-base sm:text-lg lg:text-xl font-bold mb-1 sm:mb-2">{t.home.sosúaBeachTitle}</h3>
                  <p className="text-white/90 text-[10px] sm:text-xs leading-relaxed line-clamp-2">{t.home.sosúaBeachDesc}</p>
                </div>
              </div>
            </div>

            {/* Cabarete */}
            <div className="lg:col-span-4 group cursor-pointer">
              <div className="relative h-56 sm:h-72 lg:h-[350px] rounded-2xl sm:rounded-3xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Cabarete%20Beach%20Dominican%20Republic%20kiteboarding%20paradise%20with%20colorful%20kites%20flying%20over%20turquoise%20ocean%2C%20windsurfers%2C%20golden%20sunset%2C%20palm%20trees%2C%20beach%20bars%2C%20professional%20action%20sports%20photography%2C%20vibrant%20atmosphere&width=600&height=500&seq=cabarete1&orientation=portrait" 
                  alt="Cabarete" 
                  className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3">
                    <span className="bg-rose-400 text-rose-900 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-1 rounded-full">WATER SPORTS</span>
                  </div>
                  <h3 className="text-white text-base sm:text-lg lg:text-xl font-bold mb-1 sm:mb-2">{t.home.cabareteSurfTitle}</h3>
                  <p className="text-white/90 text-[10px] sm:text-xs leading-relaxed line-clamp-2">{t.home.cabareteDesc}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-[#E8F7FA] to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-16 sm:h-24">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
            <path d="M0 0C240 40 480 60 720 60C960 60 1200 40 1440 0V120H0V0Z" fill="white"/>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 pt-8 sm:pt-12">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center gap-2 bg-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
              <i className="ri-star-fill text-[#FFD700] text-sm sm:text-base"></i>
              <span className="text-[#0A2463] text-xs sm:text-sm font-semibold">{t.home.howItWorks}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2463] mb-4 sm:mb-6">{t.home.howItWorksTitle}</h2>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg max-w-3xl mx-auto">{t.home.howItWorksSubtitle}</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-8 sm:gap-6 lg:gap-12">
            <div className="text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <span className="text-white font-bold text-lg sm:text-xl lg:text-2xl">1</span>
              </div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#0A2463] mb-3 sm:mb-4">{t.home.step1Title}</h3>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{t.home.step1Desc}</p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <span className="text-white font-bold text-lg sm:text-xl lg:text-2xl">2</span>
              </div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#0A2463] mb-3 sm:mb-4">{t.home.step2Title}</h3>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{t.home.step2Desc}</p>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[#FF6B6B] rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <span className="text-white font-bold text-lg sm:text-xl lg:text-2xl">3</span>
              </div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#0A2463] mb-3 sm:mb-4">{t.home.step3Title}</h3>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{t.home.step3Desc}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-12 sm:py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="relative h-[400px] sm:h-[500px] lg:h-[600px] rounded-2xl sm:rounded-3xl overflow-hidden">
                <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/936fb3217105ce3a6774adde1b06492a.jpeg" alt="Happy Tourists" className="w-full h-full object-cover object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-6 sm:bottom-8 lg:bottom-12 left-4 sm:left-6 lg:left-12 right-4 sm:right-6 lg:right-12">
                  <h3 className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">{t.home.bookIn3Steps}</h3>
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#00B4D8] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm sm:text-base">1</span>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-sm sm:text-base">{t.home.step1Title}</h4>
                        <p className="text-white/80 text-xs sm:text-sm">{t.home.step1Desc}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#00B4D8] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm sm:text-base">2</span>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-sm sm:text-base">{t.home.step2Title}</h4>
                        <p className="text-white/80 text-xs sm:text-sm">{t.home.step2Desc}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#00B4D8] rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm sm:text-base">3</span>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-sm sm:text-base">{t.home.step3Title}</h4>
                        <p className="text-white/80 text-xs sm:text-sm">{t.home.step3Desc}</p>
                      </div>
                    </div>
                  </div>
                  <Link to="/booking" className="mt-4 sm:mt-6 inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold text-xs sm:text-sm hover:bg-[#ff5252] transition-all whitespace-nowrap">
                    <i className="ri-calendar-check-line"></i>
                    {language === 'es' ? 'Agendar Cita' : 'Schedule Appointment'}
                  </Link>
                </div>
              </div>
            </div>

            <div className="bg-[#FAF8F3] rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 xl:p-12 order-1 lg:order-2">
              <h3 className="text-2xl sm:text-3xl font-bold text-[#0A2463] mb-6 sm:mb-8">{t.home.reserveExperience}</h3>
              <BookingForm language={language} />
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-[#E8F7FA] to-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-16 sm:h-24">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
            <path d="M0 0C240 40 480 60 720 60C960 60 1200 40 1440 0V120H0V0Z" fill="white"/>
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 pt-8 sm:pt-12">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center gap-2 bg-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-3 sm:mb-4">
              <i className="ri-star-fill text-[#FFD700] text-sm sm:text-base"></i>
              <span className="text-[#0A2463] text-xs sm:text-sm font-semibold">{t.home.trustedByThousands}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2463] mb-4 sm:mb-6">{t.home.whatGuestsSay}</h2>
            <div className="flex items-center justify-center gap-4 sm:gap-8 flex-wrap">
              <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 120 30'%3E%3Ctext x='10' y='20' font-family='Arial' font-size='16' font-weight='bold' fill='%2300AF87'%3ETripAdvisor%3C/text%3E%3C/svg%3E" alt="TripAdvisor" className="h-6 sm:h-8" />
              <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 30'%3E%3Ctext x='10' y='20' font-family='Arial' font-size='16' font-weight='bold' fill='%23FF385C'%3EViator%3C/text%3E%3C/svg%3E" alt="Viator" className="h-6 sm:h-8" />
              <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 30'%3E%3Ctext x='10' y='20' font-family='Arial' font-size='16' font-weight='bold' fill='%234285F4'%3EGoogle%3C/text%3E%3C/svg%3E" alt="Google" className="h-6 sm:h-8" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
            <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  <img src="https://readdy.ai/api/search-image?query=professional%20headshot%20portrait%20of%20happy%20female%20tourist%20smiling%2C%20casual%20vacation%20attire%2C%20friendly%20expression%2C%20simple%20neutral%20background%2C%20high%20quality%20photography&width=200&height=200&seq=review1&orientation=squarish" alt="Sarah M." className="w-full h-full object-cover object-center" />
                </div>
                <div>
                  <h4 className="font-bold text-[#0A2463] text-sm sm:text-base">Sarah M.</h4>
                  <p className="text-gray-500 text-xs sm:text-sm">New York, USA</p>
                </div>
              </div>
              <i className="ri-double-quotes-l text-[#FF6B6B] text-2xl sm:text-4xl mb-3 sm:mb-4 block"></i>
              <p className="text-gray-700 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">Absolutely amazing experience! Our driver was punctual, professional, and made us feel so welcome. The tour exceeded all expectations.</p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FFD700] text-sm sm:text-base"></i>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/7751f7f0b01cc974dbb4b1700faa7486.png" alt="Michael R." className="w-full h-full object-cover object-center" />
                </div>
                <div>
                  <h4 className="font-bold text-[#0A2463] text-sm sm:text-base">Michael R.</h4>
                  <p className="text-gray-500 text-xs sm:text-sm">Toronto, Canada</p>
                </div>
              </div>
              <i className="ri-double-quotes-l text-[#FF6B6B] text-2xl sm:text-4xl mb-3 sm:mb-4 block"></i>
              <p className="text-gray-700 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">Best tour company in Puerto Plata! They customized our itinerary perfectly. Great value for money and unforgettable memories.</p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FFD700] text-sm sm:text-base"></i>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/24a7ec930ba98da20a71d7f248d2e0d9.png" alt="Emma L." className="w-full h-full object-cover object-center" />
                </div>
                <div>
                  <h4 className="font-bold text-[#0A2463] text-sm sm:text-base">Emma L.</h4>
                  <p className="text-gray-500 text-xs sm:text-sm">London, UK</p>
                </div>
              </div>
              <i className="ri-double-quotes-l text-[#FF6B6B] text-2xl sm:text-4xl mb-3 sm:mb-4 block"></i>
              <p className="text-gray-700 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">Reliable, safe, and incredibly friendly service. The waterfall tour was breathtaking. Highly recommend Leury Tours to everyone!</p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FFD700] text-sm sm:text-base"></i>
                ))}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8928aac9e67b121d9b1718884cf49fd3.png" alt="David K." className="w-full h-full object-cover object-center" />
                </div>
                <div>
                  <h4 className="font-bold text-[#0A2463] text-sm sm:text-base">David K.</h4>
                  <p className="text-gray-500 text-xs sm:text-sm">Miami, USA</p>
                </div>
              </div>
              <i className="ri-double-quotes-l text-[#FF6B6B] text-2xl sm:text-4xl mb-3 sm:mb-4 block"></i>
              <p className="text-gray-700 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">From airport pickup to our final day, everything was seamless. The guides were knowledgeable and passionate about their country. Five stars all the way!</p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FFD700] text-sm sm:text-base"></i>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/45a54f3b7216788aee9159a905abac20.png" alt="Jessica P." className="w-full h-full object-cover object-center" />
                </div>
                <div>
                  <h4 className="font-bold text-[#0A2463] text-sm sm:text-base">Jessica P.</h4>
                  <p className="text-gray-500 text-xs sm:text-sm">Boston, USA</p>
                </div>
              </div>
              <i className="ri-double-quotes-l text-[#FF6B6B] text-2xl sm:text-4xl mb-3 sm:mb-4 block"></i>
              <p className="text-gray-700 text-xs sm:text-sm leading-relaxed mb-3 sm:mb-4">Outstanding service! Clean vehicles, friendly staff, and they went above and beyond to make our vacation special. Will definitely book again!</p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#FFD700] text-sm sm:text-base"></i>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-8 sm:mt-12">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 justify-center">
              <a 
                href="https://www.tripadvisor.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="group relative border-2 border-[#00AF87] text-[#00AF87] px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-sm whitespace-nowrap overflow-hidden transition-all duration-300 hover:text-white hover:shadow-lg hover:shadow-[#00AF87]/30 hover:-translate-y-1"
              >
                <span className="absolute inset-0 bg-[#00AF87] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
                <span className="relative flex items-center justify-center gap-2">
                  <i className="ri-star-fill"></i>
                  {t.home.readMoreTripAdvisor}
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
                </span>
              </a>
              <a 
                href="https://www.tripadvisor.com/UserReview" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="group relative border-2 border-[#FF6B6B] text-[#FF6B6B] px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-sm whitespace-nowrap overflow-hidden transition-all duration-300 hover:text-white hover:shadow-lg hover:shadow-[#FF6B6B]/30 hover:-translate-y-1"
              >
                <span className="absolute inset-0 bg-[#FF6B6B] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-right"></span>
                <span className="relative flex items-center justify-center gap-2">
                  <i className="ri-edit-line"></i>
                  {t.home.leaveReview}
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Gallery Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-[#FFF9F0] to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-16">
            <span className="text-[#FF6B6B] text-xs sm:text-sm font-bold tracking-wider uppercase animate-pulse">{t.home.unforgettableMoments}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2463] mt-3 sm:mt-4 mb-4 sm:mb-6">{t.home.happyTravelers}</h2>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg max-w-2xl mx-auto">{t.home.happyTravelersDesc}</p>
          </div>

          <GallerySlideshow />

          <div className="text-center mt-8 sm:mt-12">
            <p className="text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">{t.home.shareExperience}</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <a href="https://www.instagram.com/leury2124?igsh=bHdwNHcxbmVtOQ%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base hover:opacity-90 transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-instagram-line text-base sm:text-lg"></i>
                {t.home.followUsAt}
              </a>
              <a href="https://www.facebook.com/share/1VPJPAepMd/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#1877F2] text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold text-sm sm:text-base hover:opacity-90 transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-facebook-fill text-base sm:text-lg"></i>
                {t.home.likeOurPage}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Puerto Plata Video Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-white to-[#E8F7FA] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-[#00B4D8]/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF6B6B]/5 rounded-full translate-x-1/2 translate-y-1/2"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-8 sm:mb-12">
            <span className="text-[#FF6B6B] text-xs sm:text-sm font-bold tracking-wider uppercase">{t.home.discoverBeauty}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2463] mt-3 sm:mt-4 mb-4 sm:mb-6">{t.home.videoPuertoPlataTitle}</h2>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg max-w-3xl mx-auto leading-relaxed">{t.home.videoPuertoPlataDesc}</p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            {/* Video Container with decorative elements */}
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
              {/* Decorative border */}
              <div className="absolute -inset-1 bg-gradient-to-r from-[#00B4D8] via-[#48CAE4] to-[#FF6B6B] rounded-2xl sm:rounded-3xl opacity-30 blur-sm"></div>
              
              <div className="relative bg-black rounded-2xl sm:rounded-3xl overflow-hidden">
                <div className="aspect-video">
                  <iframe
                    src="https://www.youtube.com/embed/P4FQMqMhOj8?rel=0&modestbranding=1"
                    title="Puerto Plata - Dominican Republic"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                  ></iframe>
                </div>
              </div>
            </div>

            {/* Floating decorative elements */}
            <div className="absolute -top-4 -left-4 w-8 h-8 sm:w-12 sm:h-12 bg-[#00B4D8] rounded-full opacity-20 animate-pulse"></div>
            <div className="absolute -bottom-4 -right-4 w-10 h-10 sm:w-16 sm:h-16 bg-[#FF6B6B] rounded-full opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 -right-6 w-6 h-6 sm:w-8 sm:h-8 bg-[#48CAE4] rounded-full opacity-30 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
          </div>

          {/* Call to action */}
          <div className="text-center mt-8 sm:mt-12">
            <p className="text-gray-500 text-xs sm:text-sm mb-4">
              {language === 'es' ? '¿Listo para vivir esta experiencia?' : 'Ready to live this experience?'}
            </p>
            <Link 
              to="/booking" 
              className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-base hover:bg-[#ff5252] transition-all whitespace-nowrap"
            >
              <i className="ri-calendar-check-line"></i>
              {language === 'es' ? 'Reserva Tu Aventura' : 'Book Your Adventure'}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2463] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M30 0c16.569 0 30 13.431 30 30 0 16.569-13.431 30-30 30C13.431 60 0 46.569 0 30 0 13.431 13.431 0 30 0z\' fill=\'%23ffffff\' fill-opacity=\'1\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")'}}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12 lg:py-16 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-10 sm:mb-16">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <img src="https://static.readdy.ai/image/b7cf4d15025d84086817049d392fc8bc/8ae7b2cd5c5fe056440d4fe4564ccd66.jpeg" alt="Leury Tours" className="h-8 sm:h-10 w-auto" />
                <span className="text-lg sm:text-xl font-bold">Leury Tours</span>
              </div>
              <p className="text-white/70 text-xs sm:text-sm mb-4 sm:mb-6">{t.home.heroSubtitle}</p>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-phone-line text-[#48CAE4] text-sm"></i>
                  <span className="text-xs sm:text-sm">+1 809-461-9001</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-mail-line text-[#48CAE4] text-sm"></i>
                  <span className="text-xs sm:text-sm break-all">leury2124@gmail.com</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className="ri-map-pin-line text-[#48CAE4] text-sm"></i>
                  <span className="text-xs sm:text-sm">Puerto Plata, DR</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-white/70 mb-4 sm:mb-6">{t.footer.stayUpdated}</h4>
              <p className="text-white/70 text-xs sm:text-sm mb-3 sm:mb-4">{t.footer.getBestPrices}</p>
              <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-2">
                <div className="flex">
                  <input 
                    type="email" 
                    name="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder={t.footer.emailPlaceholder} 
                    className="flex-1 min-w-0 bg-transparent border-b border-white/30 py-2 text-xs sm:text-sm text-white placeholder-white/50 focus:outline-none focus:border-[#48CAE4]" 
                    required
                  />
                  <button 
                    type="submit"
                    disabled={newsletterStatus === 'submitting'}
                    className="ml-2 text-[#48CAE4] hover:text-white transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {newsletterStatus === 'submitting' ? (
                      <i className="ri-loader-4-line text-lg sm:text-xl animate-spin"></i>
                    ) : (
                      <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
                    )}
                  </button>
                </div>
                {newsletterStatus === 'success' && (
                  <p className="text-[#48CAE4] text-xs">
                    {language === 'es' ? '¡Gracias! Te mantendremos informado.' : 'Thank you! We\'ll keep you informed.'}
                  </p>
                )}
                {newsletterStatus === 'error' && (
                  <p className="text-[#FF6B6B] text-xs">
                    {language === 'es' ? 'Error. Intenta de nuevo.' : 'Error. Please try again.'}
                  </p>
                )}
              </form>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-white/70 mb-4 sm:mb-6">{t.footer.explore}</h4>
              <ul className="space-y-2 sm:space-y-3">
                <li><Link to="/transfers" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">{t.footer.transfers}</Link></li>
                <li><Link to="/tours" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">{t.footer.tours}</Link></li>
                <li><Link to="/tours" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">{t.footer.cityTours}</Link></li>
                <li><a href="#contact" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">{t.footer.customServices}</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-white/70 mb-4 sm:mb-6">{t.footer.followUs}</h4>
              <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                <li><a href="https://www.instagram.com/leury2124?igsh=bHdwNHcxbmVtOQ%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">Instagram</a></li>
                <li><a href="https://www.facebook.com/share/1VPJPAepMd/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">Facebook</a></li>
                <li><a href="https://tripadvisor.com" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors">TripAdvisor</a></li>
                <li><a href="https://viator.com" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm hover:text-[#48CAE4] transition-colors flex items-center gap-2">Book on Viator <i className="ri-external-link-line text-xs"></i></a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-6 sm:pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
              <p className="text-white/60 text-xs sm:text-sm text-center sm:text-left">{t.footer.allRightsReserved}</p>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                <Link to="/privacy" className="text-white/60 text-xs sm:text-sm hover:text-white transition-colors">{t.footer.privacyPolicy}</Link>
                <Link to="/terms" className="text-white/60 text-xs sm:text-sm hover:text-white transition-colors">{t.footer.termsConditions}</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function BookingForm({ language }: { language: 'en' | 'es' }) {
  const t = translations[language];
  const [formData, setFormData] = useState({
    service: '',
    date: '',
    time: '',
    passengers: '1',
    pickup: '',
    destination: '',
    name: '',
    email: '',
    phone: ''
  });
  const [price, setPrice] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Precios por servicio (basados en la página de tours)
  const servicePrices: Record<string, { price: number; label: { en: string; es: string } }> = {
    'waterfalls-damajagua': { 
      price: 55, 
      label: { en: 'Waterfalls of Damajagua Tour – Natural Adventure in Puerto Plata', es: 'Tour a las Cascadas de Damajagua – Aventura Natural en Puerto Plata' } 
    },
    'city-tour': { 
      price: 65, 
      label: { en: 'Puerto Plata City Tour - Culture, History & Local Flavor', es: 'Tour de la Ciudad de Puerto Plata - Cultura, Historia y Sabor Local' } 
    },
    'monkey-tour': { 
      price: 75, 
      label: { en: 'Monkey Tour – Fun & Interactive Experience in Puerto Plata', es: 'Tour de Monos – Experiencia Divertida e Interactiva en Puerto Plata' } 
    },
    'combined-monkey-waterfalls': {
      price: 90,
      label: { en: 'Combined Tour: Monkey & Waterfalls of Damajagua – Ultimate Adventure', es: 'Tour Combinado: Monos y Cascadas de Damajagua – Aventura Definitiva' }
    },
    'combined-monkey-city': {
      price: 90,
      label: { en: 'Combined Tour: Monkey & City Tour – Culture Meets Nature', es: 'Tour Combinado: Monos y Ciudad – Cultura y Naturaleza' }
    },
    'atv-buggies': {
      price: 95,
      label: { en: 'ATV & Buggies Adventure – Off-Road Thrills in Puerto Plata', es: 'Aventura en ATV y Buggies – Emociones Todoterreno en Puerto Plata' }
    },
    'transportation': { 
      price: 0, 
      label: { en: 'Airport Transportation', es: 'Transporte al Aeropuerto' } 
    },
    'custom-service': { 
      price: 100, 
      label: { en: 'Custom Service', es: 'Servicio Personalizado' } 
    }
  };

  useEffect(() => {
    if (formData.service && servicePrices[formData.service]) {
      const basePrice = servicePrices[formData.service].price;
      if (basePrice > 0) {
        const totalPrice = basePrice * parseInt(formData.passengers);
        setPrice(totalPrice);
      } else {
        setPrice(0);
      }
    } else {
      setPrice(0);
    }
  }, [formData.service, formData.passengers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const serviceName = formData.service && servicePrices[formData.service] 
        ? servicePrices[formData.service].label[language]
        : formData.service;

      // Guardar la reserva en Supabase
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .insert({
          customer_name: formData.name,
          customer_email: formData.email,
          customer_phone: formData.phone,
          service_type: formData.service,
          service_name: serviceName,
          amount: price,
          currency: 'USD',
          payment_status: 'pending',
          booking_date: formData.date,
          passengers: parseInt(formData.passengers),
          pickup_location: formData.pickup,
          destination: formData.destination,
          language: language
        })
        .select()
        .maybeSingle();

      if (bookingError) {
        console.error('Error al guardar la reserva:', bookingError);
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }

      if (!booking) {
        console.error('No se pudo crear la reserva');
        setSubmitStatus('error');
        setIsSubmitting(false);
        return;
      }

      // Guardar el ID de la reserva en localStorage para usarlo después del pago
      localStorage.setItem('pending_booking_id', booking.id);
      localStorage.setItem('pending_booking_data', JSON.stringify({
        service: serviceName,
        date: formData.date,
        time: formData.time,
        passengers: formData.passengers,
        price: price
      }));

      // Redirigir a PayPal con el ID de la reserva como referencia
      const paypalUrl = `https://www.paypal.com/ncp/payment/JUWXYKXZ84ACG?custom=${booking.id}`;
      window.open(paypalUrl, '_blank');

      setSubmitStatus('success');
      setFormData({
        service: '',
        date: '',
        time: '',
        passengers: '1',
        pickup: '',
        destination: '',
        name: '',
        email: '',
        phone: ''
      });
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form id="booking-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.booking.serviceType}</label>
        <select
          name="service"
          value={formData.service}
          onChange={(e) => setFormData({...formData, service: e.target.value})}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm"
          required
        >
          <option value="">{language === 'es' ? 'Selecciona un servicio' : 'Select a service'}</option>
          {Object.entries(servicePrices).map(([key, value]) => (
            <option key={key} value={key}>{value.label[language]}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.booking.date}</label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={(e) => setFormData({...formData, date: e.target.value})}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.booking.time}</label>
          <input
            type="time"
            name="time"
            value={formData.time}
            onChange={(e) => setFormData({...formData, time: e.target.value})}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.booking.passengers}</label>
        <select
          name="passengers"
          value={formData.passengers}
          onChange={(e) => setFormData({...formData, passengers: e.target.value})}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm"
          required
        >
          {[1,2,3,4,5,6,7,8,9,10,11].map(num => (
            <option key={num} value={num}>{num} {num === 1 ? t.booking.passenger : t.booking.passengersPlural}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.booking.pickupLocation}</label>
        <input
          type="text"
          name="pickup"
          value={formData.pickup}
          onChange={(e) => setFormData({...formData, pickup: e.target.value})}
          placeholder={t.booking.pickupPlaceholder}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.booking.destination}</label>
        <input
          type="text"
          name="destination"
          value={formData.destination}
          onChange={(e) => setFormData({...formData, destination: e.target.value})}
          placeholder={t.booking.destinationPlaceholder}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.booking.fullName}</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          placeholder={t.booking.namePlaceholder}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.booking.email}</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          placeholder={t.booking.emailPlaceholder}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm"
          required
        />
      </div>

      {price > 0 && (
        <div className="bg-[#00B4D8]/10 rounded-xl p-6">
          <div className="flex justify-between items-center">
            <span className="text-[#0A2463] font-semibold text-base">{t.booking.estimatedTotal}</span>
            <span className="text-[#00B4D8] font-bold text-3xl">${price}</span>
          </div>
          {formData.service && (
            <p className="text-gray-500 text-xs mt-2">
              {servicePrices[formData.service]?.price && servicePrices[formData.service].price > 0 && (
                <>
                  ${servicePrices[formData.service].price} × {formData.passengers} {parseInt(formData.passengers) === 1 ? (language === 'es' ? 'persona' : 'person') : (language === 'es' ? 'personas' : 'people')}
                </>
              )}
            </p>
          )}
        </div>
      )}

      {submitStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-green-800 text-sm font-semibold">
            {language === 'es' 
              ? '¡Reserva guardada! Se ha abierto PayPal en una nueva ventana para completar el pago.' 
              : 'Booking saved! PayPal has been opened in a new window to complete payment.'}
          </p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-800 text-sm font-semibold">
            {language === 'es'
              ? 'Error al guardar la reserva. Por favor, intenta de nuevo.'
              : 'Error saving booking. Please try again.'}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-[#FF6B6B] text-white py-4 rounded-full font-bold text-base hover:bg-[#ff5252] transition-all disabled:opacity-50 disabled:cursor-not-allowed text-center whitespace-nowrap cursor-pointer"
      >
        {isSubmitting 
          ? (language === 'es' ? 'Procesando...' : 'Processing...') 
          : (language === 'es' ? 'Continuar al Pago' : 'Continue to Payment')}
      </button>

      <div className="flex items-center justify-center gap-4 pt-4">
        <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 30'%3E%3Ctext x='5' y='20' font-family='Arial' font-size='14' font-weight='bold' fill='%23635BFF'%3ETripAdvisor%3C/text%3E%3C/svg%3E" alt="Stripe" className="h-6" />
        <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 60 30'%3E%3Ctext x='5' y='20' font-family='Arial' font-size='14' font-weight='bold' fill='%23003087'%3EPayPal%3C/text%3E%3C/svg%3E" alt="PayPal" className="h-6" />
        <div className="flex items-center gap-1">
          <i className="ri-lock-line text-gray-400 text-base"></i>
          <span className="text-gray-500 text-xs">{t.booking.secure}</span>
        </div>
      </div>
    </form>
  );
}

function ContactForm({ language }: { language: 'en' | 'es' }) {
  const t = translations[language];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams(formData).toString();

      const response = await fetch('https://readdy.ai/api/form/d65nsh594rvd6t186evg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.contact.name}</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          placeholder={t.contact.namePlaceholder}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.contact.email}</label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          placeholder={t.contact.emailPlaceholder}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.contact.phone}</label>
        <input
          type="tel"
          name="phone"
          value={formData.phone}
          onChange={(e) => setFormData({...formData, phone: e.target.value})}
          placeholder={t.contact.phonePlaceholder}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.contact.serviceInterest}</label>
        <select
          name="service"
          value={formData.service}
          onChange={(e) => setFormData({...formData, service: e.target.value})}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm"
          required
        >
          <option value="">{language === 'es' ? 'Selecciona un servicio' : 'Select a service'}</option>
          <option value={language === 'es' ? 'Cascadas de Damajagua' : 'Waterfalls of Damajagua'}>
            {language === 'es' ? 'Cascadas de Damajagua' : 'Waterfalls of Damajagua'}
          </option>
          <option value={language === 'es' ? 'Tour de Ciudad' : 'City Tour'}>
            {language === 'es' ? 'Tour de Ciudad' : 'City Tour'}
          </option>
          <option value={language === 'es' ? 'Tour de Monos' : 'Monkey Tour'}>
            {language === 'es' ? 'Tour de Monos' : 'Monkey Tour'}
          </option>
          <option value={language === 'es' ? 'Transporte' : 'Transportation'}>
            {language === 'es' ? 'Transporte' : 'Transportation'}
          </option>
          <option value={language === 'es' ? 'Servicio Personalizado' : 'Custom Service'}>
            {language === 'es' ? 'Servicio Personalizado' : 'Custom Service'}
          </option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#0A2463] mb-2">{t.contact.message}</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={(e) => {
            if (e.target.value.length <= 500) {
              setFormData({...formData, message: e.target.value});
            }
          }}
          placeholder={t.contact.messagePlaceholder}
          rows={5}
          maxLength={500}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] text-sm resize-none"
          required
        ></textarea>
        <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 {t.contact.characters}</p>
      </div>

      {submitStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-green-800 text-sm font-semibold">{t.contact.successMessage}</p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-800 text-sm font-semibold">{t.contact.errorMessage}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-[#00B4D8] text-white py-4 rounded-full font-bold text-base hover:bg-[#0096b8] transition-all disabled:opacity-50 disabled:cursor-not-allowed text-center whitespace-nowrap cursor-pointer"
      >
        {isSubmitting ? t.contact.sending : t.contact.sendMessage}
      </button>
    </form>
  );
}
